import React, {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import * as s from './styled-stations';
import {
  EmptyState,
  Icon,
  ModalClose,
  ModalConfirmation,
} from '../../components';
import { notifyError, sessionCache, useDebounce } from '../../utils';
import { icons } from '../../assets';
import {
  getCustomers,
  postNewEstacoes,
  postEditEstacoes,
  fetchZones,
  getCustomersCnpj,
  getEstacoes,
} from '../../services';

// Lazy-load do modal de cadastro/edicao — bundle pesado (Formik + yup
// + InputMask + selects de zona/cliente). So carrega quando o operador
// abre o modal.
const CadastroEstacaoModal = lazy(() =>
  import('../../components/station-form/station-form')
);

// Inline icons (lucide-style) — usados no chrome da tela. Acoes da
// linha (pencil) continuam usando <Icon> porque o componente ja
// resolve o nome.
const ISearch = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-3.5-3.5" />
  </svg>
);

const IPlus = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.4"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12 5v14" />
    <path d="M5 12h14" />
  </svg>
);

const parseEntityId = (value) => {
  const rawValue =
    value && typeof value === 'object' ? value.id || value.value : value;
  const parsedValue = Number(rawValue);

  return Number.isInteger(parsedValue) && parsedValue > 0 ? parsedValue : null;
};

const RelacaoEstacoes = () => {
  const history = useHistory();
  const { user } = useSelector((state) => state.usuario);

  // Dados servidos pelo backend, ja paginados.
  const [currentData, setCurrentData] = useState([]);
  const [amount, setAmount] = useState(0);
  const [loading, setLoading] = useState(true);

  // Filtros + paginacao
  const [filterText, setFilterText] = useState('');
  // debouncedFilterText so atualiza 350ms apos o operador parar de
  // digitar — evita re-fetch a cada keystroke.
  const debouncedFilterText = useDebounce(filterText, 350);
  const [currentPage, setCurrentPage] = useState(0);
  const [itensPerPage, setItensPerPage] = useState(10);
  const [currentEmpresa, setCurrentEmpresa] = useState('');
  const [currentZone, setCurrentZone] = useState('');

  // Dropdown options
  const [listZone, setListZone] = useState([]); // pra modal de cadastro/edit
  const [listCustomer, setListCustomer] = useState([]); // pra modal (CNPJ)
  const [listCurrentCustomers, setListCurrentCustomers] = useState([]);
  const [listCurrentZones, setListCurrentZones] = useState([]);

  // Modais
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [telaConcluido, setTelaConcluido] = useState(false);
  const [closeConfirmation, setCloseConfirmation] = useState(false);
  const [confirmationMaquinaModal, setConfirmationMaquinaModal] =
    useState(false);

  // Fluxo de edicao (inlined do antigo TableEstacoes)
  const [isModalEditVisible, setIsModalEditVisible] = useState(false);
  const [isModalEditVisibleConcluido, setIsModalEditVisibleConcluido] =
    useState(false);
  const [confirmationModal, setConfirmationModal] = useState(false);
  const [estacaoEditado, setEstacaoEditado] = useState();
  const [estacaoSelecionado, setEstacaoSelecionado] = useState({
    name: '',
    cep: '',
    endereco: '',
    complemento: '',
    latitude: '',
    longitude: '',
    cidade: '',
    zona: '',
    customer: '',
  });

  const formikInitialValuesEstacao = {
    name: '',
    cep: '',
    estado: '',
    cidade: '',
    endereco: '',
    bairro: '',
    numero: '',
    latitude: '',
    longitude: '',
    zona: '',
    customer: '',
  };

  // Token de fetch — incrementado a cada nova busca. Quando uma response
  // chega, comparamos com o token atual: se for diferente, descarta. Evita
  // race quando o operador troca filtros rapido (response antiga chega
  // depois da nova e sobrescreve dados corretos).
  const fetchTokenRef = useRef(0);

  // Fetch principal de estacoes — server-side com paginacao + filtros.
  const buscaEstacoes = useCallback(() => {
    fetchTokenRef.current += 1;
    const myToken = fetchTokenRef.current;
    setLoading(true);
    getEstacoes(
      currentPage + 1,
      itensPerPage,
      debouncedFilterText,
      currentEmpresa || undefined,
      currentZone || undefined,
      user.company ? user.company.id : ''
    )
      .then(({ data }) => {
        if (myToken !== fetchTokenRef.current) return;
        setCurrentData(data[0] || []);
        setAmount(data[1] || 0);
      })
      .catch((error) => {
        if (myToken !== fetchTokenRef.current) return;
        if (error && error.message) notifyError(error.message);
        else notifyError('Ocorreu um erro ao carregar as estações');
        setCurrentData([]);
        setAmount(0);
      })
      .finally(() => {
        if (myToken === fetchTokenRef.current) setLoading(false);
      });
  }, [
    currentPage,
    itensPerPage,
    debouncedFilterText,
    currentEmpresa,
    currentZone,
    user.company,
  ]);

  useEffect(() => {
    buscaEstacoes();
    // Cleanup: bump token pra ignorar response em-voo quando o componente
    // desmontar (evita setState em componente desmontado).
    return () => {
      fetchTokenRef.current += 1;
    };
  }, [buscaEstacoes, telaConcluido]);

  // Reseta pra pagina 1 quando filtros/itensPerPage mudam.
  useEffect(() => {
    setCurrentPage(0);
  }, [debouncedFilterText, itensPerPage, currentEmpresa, currentZone]);

  // Carga dos dropdowns (uma vez ao mount). sessionCache evita re-fetch
  // entre montagens — listas mudam raramente. Scope inclui o customer
  // logado pra nao misturar cache entre tenants.
  const buscaZonaList = useCallback(() => {
    sessionCache('zonas', user.company ? `c-${user.company}` : 'all', () =>
      fetchZones(!!user.company)
    )
      .then(({ data }) => {
        const filterZonaList = [];
        const filterZona = [{ label: 'Selecione', value: '' }];
        data.forEach((zona) => {
          filterZonaList.push({ label: zona.name, value: zona.id });
          filterZona.push({ label: zona.name, value: zona.id });
        });
        setListZone(filterZonaList);
        setListCurrentZones(filterZona);
      })
      .catch((error) => {
        if (error && error.message) notifyError(error.message);
        else notifyError('Ocorreu um erro ao carregar as zonas');
      });
  }, [user.company]);

  const buscaCustomerCnpj = useCallback(() => {
    sessionCache(
      'customersCnpj',
      user.company ? `c-${user.company}` : 'all',
      () => getCustomersCnpj(user.company ? user.company.id : '')
    )
      .then(({ data }) => {
        const filterCustomers = data.name.map((name, i) => ({
          label: `${name} - ${data.cnpj[i]}`,
          value: data.id[i],
        }));
        setListCustomer(filterCustomers);
      })
      .catch((error) => {
        if (error && error.message) notifyError(error.message);
        else notifyError('Ocorreu um erro ao carregar os clientes');
      });
  }, [user.company]);

  const buscaCustomers = useCallback(() => {
    sessionCache('allCustomers', 'all', () => getCustomers('', '', '', '', ''))
      .then(({ data }) => {
        const filterCustomers = [{ label: 'Selecione', value: '' }];
        (data[0] || []).forEach((customer) => {
          filterCustomers.push({ label: customer.name, value: customer.id });
        });
        setListCurrentCustomers(filterCustomers);
      })
      .catch((error) => {
        if (error && error.message) notifyError(error.message);
        else notifyError('Ocorreu um erro ao carregar os clientes');
      });
  }, []);

  useEffect(() => {
    buscaZonaList();
    buscaCustomers();
    buscaCustomerCnpj();
  }, [buscaZonaList, buscaCustomers, buscaCustomerCnpj]);

  // Cadastro / edicao — preserva contratos antigos com CadastroEstacaoModal.
  const cadastrarEstacaoNovo = (values) => {
    const customerId = parseEntityId(values.customer);
    const zoneId = parseEntityId(values.zona);
    const companyId = parseEntityId(user.company);

    if (!customerId || !zoneId || !companyId) {
      notifyError('Cliente, zona ou empresa inválidos');
      return;
    }

    postNewEstacoes({
      name: values.name,
      postalcode: values.cep,
      state: values.estado,
      city: values.cidade,
      district: values.bairro,
      address: values.endereco,
      addressnumber: values.numero,
      lat: values.latitude,
      lng: values.longitude,
      company: companyId,
      customer: customerId,
      zone: zoneId,
    })
      .then(() => setTelaConcluido(true))
      .catch((error) => {
        if (error && error.message) notifyError(error.message);
        else notifyError('Ocorreu um erro ao salvar a estação');
      });
  };

  const editarEstacao = (values) => {
    const customerId = parseEntityId(values.customer);
    const zoneId = parseEntityId(values.zona);

    if (!customerId || !zoneId) {
      notifyError('Cliente ou zona inválidos');
      setConfirmationModal(false);
      return;
    }

    postEditEstacoes(estacaoSelecionado.id, {
      name: values.name,
      postalcode: values.cep,
      state: values.estado,
      city: values.cidade,
      district: values.bairro,
      address: values.endereco,
      addressnumber: values.numero,
      lat: values.latitude,
      lng: values.longitude,
      customer: customerId,
      zone: zoneId,
    })
      .then(() => {
        setIsModalEditVisible(false);
        setConfirmationModal(false);
        setIsModalEditVisibleConcluido(true);
        buscaEstacoes();
      })
      .catch((error) => {
        if (error && error.message) notifyError(error.message);
        else notifyError('Ocorreu um erro ao salvar a estação');
        setConfirmationModal(false);
      });
  };

  // Abre o modal de edicao mapeando o shape do backend pro shape do formik.
  const handleOpenEdit = (row) => {
    setEstacaoSelecionado({
      id: row.id ? row.id : '',
      name: row.name ? row.name : '',
      cep: row.postalcode ? row.postalcode : '',
      estado: row.state ? row.state : '',
      cidade: row.city ? row.city : '',
      endereco: row.address ? row.address : '',
      bairro: row.district ? row.district : '',
      numero: row.addressnumber ? row.addressnumber : '',
      latitude: row.lat ? row.lat : '',
      longitude: row.lng ? row.lng : '',
      zona: row.zone ? row.zone : '',
      customer: row.customer ? row.customer : '',
    });
    setIsModalEditVisible(true);
  };

  const pageCount = Math.ceil(amount / itensPerPage) || 0;

  // Lista de paginas com elipses (1, 2, 3, …, n-2, n-1, n) — match do
  // padrao .pager do design.
  const pagList = useMemo(() => {
    const list = [];
    const total = pageCount;
    const pg = currentPage + 1;
    if (total <= 7) {
      for (let i = 1; i <= total; i += 1) list.push(i);
    } else {
      list.push(1, 2, 3);
      if (pg > 5) list.push('…');
      if (pg > 3 && pg < total - 2) list.push(pg);
      if (pg < total - 4) list.push('…');
      list.push(total - 2, total - 1, total);
    }
    return Array.from(new Set(list));
  }, [pageCount, currentPage]);

  return (
    <s.Container className="fade-in">
      <s.PageHeader>
        <s.PageHeaderText>
          <s.PageTitle>Estações</s.PageTitle>
          <s.PageSub>
            Cadastre estações com cliente, endereço e zona operacional.
          </s.PageSub>
        </s.PageHeaderText>
        <s.PageCount>
          <b>{amount}</b> {amount === 1 ? 'estação' : 'estações'}
        </s.PageCount>
      </s.PageHeader>

      <s.Filters>
        <s.Search>
          {ISearch}
          <input
            value={filterText}
            onChange={(event) => setFilterText(event.target.value)}
            placeholder="Pesquise por estação, cliente ou estado"
          />
        </s.Search>

        <s.SelCell>
          <select
            value={currentEmpresa}
            onChange={(e) => setCurrentEmpresa(e.target.value)}
          >
            <option value="">Cliente</option>
            {listCurrentCustomers
              .filter((o) => o.value !== '')
              .slice()
              .sort((a, b) =>
                (a.label || '').localeCompare(b.label || '', 'pt-BR', {
                  sensitivity: 'base',
                })
              )
              .map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
          </select>
        </s.SelCell>

        <s.SelCell>
          <select
            value={currentZone}
            onChange={(e) => setCurrentZone(e.target.value)}
          >
            <option value="">Estado</option>
            {listCurrentZones
              .filter((o) => o.value !== '')
              .slice()
              .sort((a, b) =>
                (a.label || '').localeCompare(b.label || '', 'pt-BR', {
                  sensitivity: 'base',
                })
              )
              .map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
          </select>
        </s.SelCell>

        <s.Cta type="button" onClick={() => setIsModalVisible(true)}>
          <span className="ic">{IPlus}</span>
          <span className="tx">
            <span className="t">Cadastro</span>
            <span className="s">Inserir estação</span>
          </span>
        </s.Cta>
      </s.Filters>

      <s.TableWrap>
        <s.Table>
          <s.Th>Editar</s.Th>
          <s.Th>Estação</s.Th>
          <s.Th>Cliente</s.Th>
          <s.Th>Estado</s.Th>

          {loading ? (
            Array.from(
              { length: itensPerPage },
              (_, i) => `sk-${itensPerPage}-${i}`
            ).map((key) => (
              <s.Tr key={key}>
                <s.SkeletonCell $w="40%" />
                <s.SkeletonCell $w="60%" />
                <s.SkeletonCell $w="55%" />
                <s.SkeletonCell $w="65%" />
              </s.Tr>
            ))
          ) : currentData.length ? (
            currentData.map((row) => (
              <s.Tr key={row.id}>
                <s.Tc data-label="Editar">
                  <s.RowActions>
                    <s.IconBtn
                      type="button"
                      title="Editar estação"
                      aria-label={`Editar estação ${row.name || ''}`}
                      onClick={() => handleOpenEdit(row)}
                    >
                      <Icon name="pencil" size={16} />
                    </s.IconBtn>
                  </s.RowActions>
                </s.Tc>
                <s.Tc className="estacao" data-label="Estação">
                  {row.name}
                </s.Tc>
                <s.Tc className="cliente" data-label="Cliente">
                  {row.customer ? row.customer.name : '—'}
                </s.Tc>
                <s.Tc className="estado" data-label="Estado">
                  {row.zone ? row.zone.name : '—'}
                </s.Tc>
              </s.Tr>
            ))
          ) : (
            <s.EmptyRow>
              <EmptyState
                title="Nenhuma estação encontrada"
                description="Ajuste a busca ou os filtros e tente novamente."
                actionLabel="Limpar filtros"
                onAction={() => {
                  setFilterText('');
                  setCurrentEmpresa('');
                  setCurrentZone('');
                }}
              />
            </s.EmptyRow>
          )}
        </s.Table>
      </s.TableWrap>

      <s.Pager>
        <button
          className="btn"
          type="button"
          onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
          disabled={currentPage === 0 || pageCount === 0}
        >
          Anterior
        </button>
        <div className="pages">
          {pagList.map((p, idx) =>
            p === '…' ? (
              // eslint-disable-next-line react/no-array-index-key
              <span key={`dots-${idx}`} className="dots">
                …
              </span>
            ) : (
              <button
                key={p}
                type="button"
                className={`pg ${currentPage + 1 === p ? 'on' : ''}`}
                onClick={() => setCurrentPage(p - 1)}
              >
                {p}
              </button>
            )
          )}
        </div>
        <button
          className="btn"
          type="button"
          onClick={() => setCurrentPage((p) => Math.min(pageCount - 1, p + 1))}
          disabled={currentPage >= pageCount - 1 || pageCount === 0}
        >
          Próximo
        </button>
        <div className="exhibit">
          exibir
          <select
            value={itensPerPage}
            onChange={(e) => setItensPerPage(Number(e.target.value))}
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
          </select>
        </div>
      </s.Pager>

      {/* Modal de cadastro (novo) */}
      {isModalVisible && (
        <Suspense fallback={null}>
          <CadastroEstacaoModal
            onClose={() => {
              setIsModalVisible(false);
              setTelaConcluido(false);
            }}
            onCloseConfirmation={setCloseConfirmation}
            submit={cadastrarEstacaoNovo}
            estacaoConcluido={telaConcluido}
            setEstacaoConcluido={setTelaConcluido}
            setConfirmationModal={() => {}}
            formikInitialValues={formikInitialValuesEstacao}
            listCustomer={listCustomer}
            listZona={listZone}
            title="Cadastro de Estação"
            dadosAbaixo="Preencha os dados abaixo:"
            msgButton="Ao cadastrar, um convite será enviado por e-mail"
            labelButton="CADASTRAR"
            sucesso="Estação cadastrada com sucesso!"
            iconFinal={icons.EstacoesIconFinish}
            setConfirmationMaquinaModal={setConfirmationMaquinaModal}
          />
        </Suspense>
      )}

      {/* Modal de edicao */}
      {isModalEditVisible && (
        <Suspense fallback={null}>
          <CadastroEstacaoModal
            onClose={() => {
              setIsModalEditVisible(false);
            }}
            onCloseConfirmation={setCloseConfirmation}
            submit={setEstacaoEditado}
            setConfirmationModal={setConfirmationModal}
            setEstacaoConcluido={setIsModalEditVisibleConcluido}
            estacaoConcluido={isModalEditVisibleConcluido}
            formikInitialValues={estacaoSelecionado}
            listCustomer={listCustomer}
            listZona={listZone}
            title="Editar Estação"
            dadosAbaixo="Modifique os dados abaixo:"
            msgButton="Confirme suas modificações"
            labelButton="SALVAR"
            sucesso="Estação editada com sucesso!"
            estadoValue={estacaoSelecionado.estado}
            iconFinal={icons.EstacoesIconEdit}
          />
        </Suspense>
      )}

      {/* Tela "concluido" da edicao (passo final do mesmo modal) */}
      {isModalEditVisibleConcluido && (
        <Suspense fallback={null}>
          <CadastroEstacaoModal
            onClose={() => {
              setIsModalEditVisibleConcluido(false);
            }}
            onCloseConfirmation={setCloseConfirmation}
            setEstacaoConcluido={setIsModalEditVisibleConcluido}
            estacaoConcluido={isModalEditVisibleConcluido}
            formikInitialValues={estacaoSelecionado}
            listCustomer={listCustomer}
            listZona={listZone}
            title="Editar Estação"
            sucesso="Estação editada com sucesso!"
            iconFinal={icons.EstacoesIconEdit}
          />
        </Suspense>
      )}

      {/* Confirmacao do save da edicao */}
      {confirmationModal && (
        <ModalConfirmation
          onClose={() => {
            editarEstacao(estacaoEditado);
          }}
          onCloseConfirmation={setConfirmationModal}
        />
      )}

      {/* Modal de "tem certeza que quer fechar?" (descartar edicoes) */}
      {closeConfirmation && (
        <ModalClose
          onClose={() => {
            setIsModalVisible(false);
            setIsModalEditVisible(false);
            setTelaConcluido(false);
            setCloseConfirmation(false);
          }}
          onCloseConfirmation={setCloseConfirmation}
        />
      )}

      {/* Pos-cadastro: oferece criar uma maquina ja na estacao nova */}
      {confirmationMaquinaModal && (
        <ModalConfirmation
          labelTitle="Deseja cadastrar uma máquina?"
          labelConfirmation="SIM"
          labelClose="NÃO"
          onClose={() => {
            history.push('/machines');
          }}
          onCloseConfirmation={setConfirmationMaquinaModal}
        />
      )}
    </s.Container>
  );
};

export default RelacaoEstacoes;
